import {
	type BaseState,
	type GameDefinition,
	type MoveOutcome,
	type PlayerIndex,
	reject,
	resultFromScores,
} from "../types";
import { QUESTIONS, type Question } from "./questions";

export const QUIZ_LENGTH = 7;
/** How long a question stays open. */
export const QUIZ_QUESTION_MS = 15_000;
/** How long the answer is shown before the next question. */
export const QUIZ_REVEAL_MS = 3_500;
/** Points for a correct answer, plus a bonus for the fastest correct one. */
export const QUIZ_CORRECT_POINTS = 1;
export const QUIZ_SPEED_BONUS = 1;

export interface QuizAnswer {
	/** `-1` when hidden from the other player. */
	choice: number;
	at: number;
}

export interface QuizState extends BaseState {
	kind: "quiz";
	questions: Question[];
	index: number;
	phase: "question" | "reveal";
	/** When the open question closes, or when the reveal ends. */
	endsAt: number;
	answers: [QuizAnswer | null, QuizAnswer | null];
	/** Points won on the question currently being revealed. */
	lastAward: [number, number];
}

export interface QuizMove {
	choice: number;
}

function scoreQuestion(state: QuizState, now: number): QuizState {
	const question = state.questions[state.index];
	const award: [number, number] = [0, 0];

	if (question) {
		const correct: { player: PlayerIndex; at: number }[] = [];
		for (const player of [0, 1] as const) {
			const answer = state.answers[player];
			if (answer && answer.choice === question.answer) correct.push({ player, at: answer.at });
		}
		correct.sort((a, b) => a.at - b.at);
		correct.forEach((entry, rank) => {
			award[entry.player] = QUIZ_CORRECT_POINTS + (rank === 0 ? QUIZ_SPEED_BONUS : 0);
		});
	}

	return {
		...state,
		scores: [state.scores[0] + award[0], state.scores[1] + award[1]],
		lastAward: award,
		phase: "reveal",
		endsAt: now + QUIZ_REVEAL_MS,
	};
}

function nextQuestion(state: QuizState, now: number): QuizState {
	const index = state.index + 1;
	if (index >= state.questions.length) {
		return { ...state, result: resultFromScores(state.scores), endsAt: now };
	}
	return {
		...state,
		index,
		phase: "question",
		endsAt: now + QUIZ_QUESTION_MS,
		answers: [null, null],
		lastAward: [0, 0],
	};
}

export const quiz: GameDefinition<QuizState, QuizMove> = {
	id: "quiz",
	category: "education",
	title: "Quiz Duel",
	emoji: "⚡",
	blurb: "Maths, words and world facts — answer faster than your friend.",
	howTo: [
		"You both see the same question at the same time.",
		"Tap an answer before the timer runs out.",
		"A right answer is a point — the fastest right answer earns a bonus point.",
		"Most points after 7 questions wins.",
	],

	create(rng, now) {
		return {
			kind: "quiz",
			questions: rng.sample(QUESTIONS, QUIZ_LENGTH),
			index: 0,
			phase: "question",
			endsAt: now + QUIZ_QUESTION_MS,
			answers: [null, null],
			lastAward: [0, 0],
			// Nobody's "turn" — both players answer at once.
			turn: null,
			scores: [0, 0],
			result: null,
		};
	},

	parseMove(raw) {
		if (typeof raw !== "object" || raw === null) return null;
		const choice = (raw as { choice?: unknown }).choice;
		if (typeof choice !== "number" || !Number.isInteger(choice)) return null;
		if (choice < 0 || choice > 3) return null;
		return { choice };
	},

	apply(state, player, move, now): MoveOutcome<QuizState> {
		if (state.result) return reject("This game is already finished.");
		if (state.phase !== "question") return reject("Hold on — the answer is showing.");
		if (state.answers[player]) return reject("You've already picked an answer.");

		const question = state.questions[state.index];
		if (!question || move.choice >= question.choices.length) {
			return reject("That isn't one of the answers.");
		}

		const answers: [QuizAnswer | null, QuizAnswer | null] = [...state.answers];
		answers[player] = { choice: move.choice, at: now };

		const next = { ...state, answers };
		// Both in: reveal straight away rather than making them watch the timer.
		return {
			ok: true,
			state: answers[0] && answers[1] ? scoreQuestion(next, now) : next,
		};
	},

	legalMoves(state, player) {
		// The quiz has no turns: you may answer until you have.
		if (state.result || state.phase !== "question" || state.answers[player]) return [];
		const question = state.questions[state.index];
		if (!question) return [];
		return question.choices.map((_, choice) => ({ choice }));
	},

	tick(state, now) {
		if (state.result || now < state.endsAt) return null;
		return state.phase === "question" ? scoreQuestion(state, now) : nextQuestion(state, now);
	},

	nextTickAt(state) {
		return state.result ? null : state.endsAt;
	},

	redact(state, viewer) {
		const hidden: Question = {
			id: "",
			category: "maths",
			prompt: "",
			choices: [],
			answer: -1,
		};
		return {
			...state,
			questions: state.questions.map((question, i) => {
				if (i > state.index) return hidden;
				// The answer key only goes out once the question is closed.
				if (i === state.index && state.phase === "question") return { ...question, answer: -1 };
				return question;
			}),
			answers: state.answers.map((answer, i) => {
				if (answer === null) return null;
				if (state.phase === "reveal" || i === viewer) return answer;
				// You can see *that* they answered, not what they picked.
				return { choice: -1, at: answer.at };
			}) as [QuizAnswer | null, QuizAnswer | null],
		};
	},
};
